<template>
  <!-- Not assigned: violet outline CTA -->
  <button
    v-if="featureEnabled && !filled && !readonly"
    type="button"
    class="ajct-cta"
    data-testid="assigned-job-category-cta"
    aria-label="Assign job category"
    @click="onAssign"
  >
    <JobCategoryIcon class="ajct-icon" />
    <PlusIcon class="ajct-plus" />
    <span class="ajct-cta-label">Job Category</span>
  </button>

  <!-- Assigned: removable gray tag -->
  <span
    v-else-if="featureEnabled && filled"
    class="ajct-tag"
    data-testid="assigned-job-category-tag"
  >
    <JobCategoryIcon class="ajct-icon" />

    <span class="ajct-name">{{ jobCategoryName }}</span>

    <span v-if="totalScore !== null" class="ajct-score" data-testid="assigned-job-category-score">
      {{ totalScore }}
    </span>

    <EXTooltip
      v-if="!readonly"
      class="ajct-close-tip"
      title="Remove job category"
      placement="top"
      :delay="100"
      show-on-child-focus
    >
      <button
        type="button"
        class="ajct-close"
        aria-label="Remove job category"
        data-testid="assigned-job-category-close"
        @click="onUnassign"
      >
        <TagCloseIcon />
      </button>
    </EXTooltip>
  </span>
</template>

<script setup>
import { getCurrentInstance, onMounted, ref } from "vue";

import { LD_FLAG_IDEA_MDP_566 } from "@components/constants/launchDarklyFlagKeys";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import JobCategoryIcon from "./icons/JobCategoryIcon.vue";
import PlusIcon from "./icons/PlusIcon.vue";
import TagCloseIcon from "./icons/TagCloseIcon.vue";

/**
 * ER-11421, ER-12044 — AssignedJobCategoryTag (position form Job Category control).
 *
 * Tag/chip control for assigning a Job Category to a position, with two states:
 * - Not Assigned (`filled=false`): a light-violet outline "+ Job Category" CTA that
 *   emits `assign` (the parent opens the job category picker).
 * - Assigned (`filled=true`): a gray removable tag showing the job category name, a
 *   Total Score badge (integer 0–73) and a close (X) button that emits `unassign`.
 *
 * Controlled presentational component: the parent owns the `filled` state and the
 * `jobCategoryName` / `totalScore`. No API calls inside the component.
 *
 * Shipped behind the linked idea flag (idea-MDP-566, "Job Category — Equal-Value
 * Grouping for Fair Pay (EU Directive Alignment)") — the same kill switch that gates
 * JobCategoriesList and AddJobCategoryWizard. Renders by default; set the flag to
 * false to hide the control until a screen wires it.
 *
 * NOTE (re-scope requested 2026-09-11): Eric Kish's comment re-points the design to
 * Figma node 47421-36969 ("AssignedJobCategoryTag"), superseding the stale node
 * 47421-36966 still in the ticket description (that node/copy belonged to the sibling
 * AssignedJobFamilyTag). Re-verified live against node 47421-36969 this pass and fixed
 * the drift: the CTA copy is "Job Category" (was "Job Family"), the glyph is a 2x2
 * rounded-square "category" icon (was a layers glyph), the assigned-state category
 * icon is a dedicated gray (#666666, not inherited from the name text colour), the
 * score badge is an outlined pill (border #475467, no fill) showing the Total Score
 * integer, and the close icon is #98a2b3.
 *
 * ER-12044 changes: `level: String` prop replaced with `totalScore: Number` (the
 * category's Total Score, 0–73, or null when unscored); badge hidden when totalScore
 * is null; added `readonly: Boolean` to hide CTA and close button (for Retired/Archived
 * positions or no edit permission); read-only + not filled renders nothing.
 */
const props = defineProps({
  /** False = Not Assigned state (CTA); True = Assigned state (removable tag). */
  filled: {
    type: Boolean,
    default: false,
  },
  /** Assigned job category name, e.g. "Electrician". Shown only when `filled`. */
  jobCategoryName: {
    type: String,
    default: "",
  },
  /** Job category Total Score (integer 0–73), or null when unscored. Badge hidden when null. */
  totalScore: {
    type: Number,
    default: null,
  },
  /** Read-only mode: hides CTA and close button. When readonly + !filled, renders nothing. */
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["assign", "unassign"]);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

function onAssign() {
  emit("assign");
}

function onUnassign() {
  emit("unassign");
}
</script>

<style scoped>
.ajct-icon,
.ajct-plus {
  flex-shrink: 0;
}

/* Not-assigned CTA button (violet outline) */
.ajct-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 6px 10px;
  background: #f9f5ff;
  border: 1px solid #d6bbfb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  color: #53389e;
  cursor: pointer;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
}

.ajct-cta:hover {
  background: #f4ebff;
  border-color: #d6bbfb;
}

.ajct-cta:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.ajct-cta-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

/* Assigned tag (pill) */
.ajct-tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 6px 4px 10px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.04);
  color: #344054;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
}

.ajct-tag .ajct-icon {
  color: #666666;
}

.ajct-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

.ajct-score {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: transparent;
  border: 1.5px solid #475467;
  border-radius: 9999px;
  color: #344054;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.ajct-close-tip {
  display: inline-flex;
  line-height: 0;
}

.ajct-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  border-radius: 3px;
  cursor: pointer;
}

.ajct-close:hover {
  background: #f2f4f7;
}

.ajct-close:focus-visible {
  outline: 2px solid #475467;
  outline-offset: 2px;
}
</style>
